import { API_BASE } from "../services/api";

import React, { useState } from "react";
import {
  Grid,
  Paper,
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  CssBaseline,
  Alert,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const theme = createTheme();

function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // 백엔드 응답 형식 (v8)
  interface LoginResponse {
    timestamp: string;
    success: boolean;
    message: string;
    error: string;
    data: {
      user_id: string;
      org_id?: string | null;
      tier: number;                 // 0/1/2
      access_token: string;
      refresh_token: string;
      org_name: string;
    };
  }

  const handleLogin = async () => {
    setErrorMsg(null);
    setIsLoading(true);

    try {
      const res = await axios.post<LoginResponse>(`${API_BASE}auth/login`, {
        email,
        password: pw,
      });

      if (res.data.success && res.data.data) {
        const { user_id, org_id, tier, access_token, refresh_token, org_name} = res.data.data;

        localStorage.setItem("user_id", user_id);
        if (org_id) localStorage.setItem("org_id", org_id);
        else localStorage.removeItem("org_id");

        localStorage.setItem("tier", String(tier)); // "0"이면 오너
        localStorage.setItem("access_token", access_token);
        localStorage.setItem("refresh_token", refresh_token);
        localStorage.setItem("org_name", org_name);

        navigate("/");
      } else {
        setErrorMsg(res.data.error || res.data.message || "Login failed");
      }
    } catch (error: any) {
      console.error(error);
      setErrorMsg("Error occurred during login request.");
    } finally {
      setIsLoading(false);
    }
  };

  // Enter 키 입력 시 로그인
  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleLogin();
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Grid container component="main" sx={{ height: "100vh" }}>
        {/* 왼쪽 배경 이미지 */}
        <Grid
          item
          xs={false}
          sm={4}
          md={8}
          sx={{
            backgroundImage: `url("/img/mainIMG.jpg")`,
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
            backgroundPosition: "left center",
          }}
        />

        {/* 오른쪽 (로그인) - 중앙 정렬 */}
        <Grid
          item
          xs={12}
          sm={8}
          md={4}
          component={Paper}
          elevation={6}
          square
          container
          justifyContent="center"
          alignItems="center"
        >
          <Grid item sx={{ width: "80%", maxWidth: 400 }}>
            <Box display="flex" flexDirection="column" alignItems="center" mb={1}>
              <Box component="img" src="/img/krCI.jpg" alt="Korean Register" sx={{ height: 60, mb: 1 }} />
              <Typography variant="h4" component="h1" fontWeight={700} letterSpacing={2} sx={{ color: "primary.main" }}>
                KR AI Data Lake
              </Typography>
            </Box>

            {/* 에러 메시지 */}
            {errorMsg && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errorMsg}
              </Alert>
            )}

            {/* Email */}
            <TextField
              margin="normal"
              required
              fullWidth
              label="Email"
              type="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={onKeyDown}
            />

            {/* Password */}
            <TextField
              margin="normal"
              required
              fullWidth
              label="Password"
              type="password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              onKeyDown={onKeyDown}
            />

            {/* 로그인 or 로딩 */}
            {isLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Button type="button" fullWidth variant="contained" sx={{ mt: 2, mb: 2 }} onClick={handleLogin}>
                Sign In
              </Button>
            )}

            <Box sx={{ mt: 5, textAlign: "center" }}>
              <Typography variant="caption" color="text.secondary">
                © 2025 Korean Register. All rights reserved.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Grid>
    </ThemeProvider>
  );
}

export default LoginPage;
